import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import React, { useCallback, useMemo } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SemiCircleGraph from 'core/components/graphs/SemiCircleGraph'
import HelpContainer from 'core/components/HelpContainer'
import Card from 'core/elements/card'
import CardBody from 'core/elements/card/CardBody'
import Divider from 'core/elements/Divider'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { formatMetricValue } from 'core/utils/unitUtils'
import TrendGraphWidget from './TrendGraphWidget'

const defaultStats = { current: 0, max: 0, percent: 0 }

const getBackgroundColor = ({ theme, percent }) => {
  if (percent < 50) {
    return theme.palette.graphGreen[100]
  } else if (percent < 80) {
    return theme.palette.graphYellow[100]
  } else {
    return theme.palette.graphRed[100]
  }
}

const getBorderColor = ({ theme, percent }) => {
  if (percent < 50) {
    return theme.palette.graphGreen[500]
  } else if (percent < 80) {
    return theme.palette.graphYellow[500]
  } else {
    return theme.palette.graphRed[500]
  }
}

interface Allocation {
  allocated: number
  capacity: number
}

interface Props {
  title: string | React.ReactNode
  units: string
  stats: typeof defaultStats
  precision?: number
  usedText?: string
  icon?: string
  primary?: string
  metricKey?: string
  showEmptyGraphMessage?: boolean
  resourceId?: string
  allocation?: boolean
  hostIds?: string[]
  dialTooltipMsg?: string | (() => React.ReactNode)
  chartTooltipMsg?: string | (() => React.ReactNode)
  graphUnit?: string
  graphTitle?: string
}

export default function UsageWidget({
  title,
  precision = 1,
  units = '',
  usedText = 'used',
  graphUnit = '',
  stats = defaultStats,
  icon,
  metricKey,
  showEmptyGraphMessage = false,
  resourceId,
  allocation,
  hostIds,
  dialTooltipMsg = '',
  chartTooltipMsg = '',
  graphTitle,
  ...rest
}: Props) {
  const classes = useStyles()
  const theme: any = useTheme()
  const { current, max, percent } = stats

  const available = max - current

  const { formattedValue: formattedCurrent, displayUnit: currentDisplayUnit } = formatMetricValue(
    current,
    units,
  )
  const { formattedValue: formattedMax, displayUnit: maxDisplayUnit } = formatMetricValue(
    max,
    units,
  )
  const { formattedValue: formattedAvailable, displayUnit: availableDisplayUnit } =
    formatMetricValue(available, units)

  const availableStr = available < 0 ? '0' : formattedAvailable
  const percentage = allocation && Math.round(percent) > 100 ? 100 : Math.round(percent)

  // Show units only for non-vCPU metrics only
  const showUnits = useCallback(
    (displayUnit: string) => (units === 'vCPUs' ? 'vCPUs' : displayUnit),
    [units],
  )

  const maxOutLabel = `out of ${formattedMax} ${showUnits(maxDisplayUnit)}`

  const data = useMemo(
    () => [
      {
        value: 100 - percentage,
        name: '',
        color: 'tray' as const,
      },
      {
        value: percentage,
        name: '',
        color: getBackgroundColor({ theme, percent: percentage }),
        strokeColor: getBorderColor({ theme, percent: percentage }),
      },
    ],
    [percentage, theme],
  )

  return (
    <div>
      <Card className={classes.card} withCustomBody>
        <CardBody className={classes.cardBody}>
          <div className={classes.header}>
            <div className={classes.iconContainer}>
              <FontAwesomeIcon className={classes.icon} solid>
                {icon}
              </FontAwesomeIcon>
            </div>
            <Text variant="subtitle2">{title}</Text>
            {dialTooltipMsg && <HelpContainer title={dialTooltipMsg} />}
          </div>
          <SemiCircleGraph data={data} percent={percent / 100} primary={maxOutLabel} {...rest} />
          <div className={classes.statLabels}>
            <div
              className={clsx(classes.leftStatLabel, allocation && classes.leftStatLabelAllocation)}
            >
              <Text variant="subtitle2" className={classes.smallLineHeight}>
                {formattedCurrent}{' '}
                <span className={classes.displayUnit}>{showUnits(currentDisplayUnit)}</span>
              </Text>
              <Text variant="body2">{allocation ? 'allocated' : usedText}</Text>
            </div>
            <div
              className={clsx(
                classes.rightStatLabel,
                allocation && classes.rightStatLabelAllocation,
              )}
            >
              <Text variant="subtitle2" className={classes.smallLineHeight}>
                {availableStr}{' '}
                <span className={classes.displayUnit}>{showUnits(availableDisplayUnit)}</span>
              </Text>
              <Text variant="body2">{allocation ? 'unallocated' : 'available'}</Text>
            </div>
          </div>
        </CardBody>
        {metricKey && (
          <>
            <Divider className={classes.divider} />
            <CardBody>
              <TrendGraphWidget
                title={graphTitle ? graphTitle : typeof title === 'string' ? title : ''}
                units={units}
                graphUnit={graphUnit}
                metricKey={metricKey}
                resourceId={resourceId}
                stats={stats}
                precision={precision}
                resources={hostIds}
                tooltipMsg={typeof chartTooltipMsg === 'string' ? chartTooltipMsg : ''}
              />
            </CardBody>
          </>
        )}
        {showEmptyGraphMessage && (
          <>
            <Divider className={classes.divider} />
            <CardBody className={classes.graphCardBody}>
              <div>
                <Text variant="subtitle2">{title}</Text>
              </div>
              <div className={classes.graphCardMessage}>
                <Text variant="caption1">{'No Data Available'}</Text>
              </div>
            </CardBody>
          </>
        )}
      </Card>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  card: {
    display: 'grid',
    gridTemplateRows: 'max-content max-content',
  },
  cardBody: {
    display: 'grid',
    justifyItems: 'center',
    paddingBottom: 32,
    '& path[name="unknown"]': {
      stroke: theme.components.graph.stroke,
      fill: theme.components.graph.tray,
    },
  },
  header: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    paddingBottom: 12,
  },
  iconContainer: {
    background: theme.palette.grey[100],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
  },
  icon: {
    fontSize: '16px',
    color: theme.palette.grey[500],
  },
  statLabels: {
    position: 'relative',
    height: 24,
    top: -4,
  },
  smallLineHeight: {
    lineHeight: 1,
  },
  leftStatLabel: {
    position: 'absolute',
    left: -114,
    display: 'grid',
    justifyContent: 'center',
    textAlign: 'center',
    width: 'max-content',
  },
  leftStatLabelAllocation: {
    left: -128,
  },
  rightStatLabel: {
    position: 'absolute',
    left: 68,
    display: 'grid',
    justifyContent: 'center',
    textAlign: 'center',
    width: 'max-content',
  },
  rightStatLabelAllocation: {
    left: 60,
  },
  displayUnit: {
    fontSize: '12px',
    fontWeight: 'normal',
  },
  units: {
    color: theme.components.typography.passive,
    fontWeight: 'normal',
  },
  divider: {
    background: theme.palette.grey[200],
    margin: theme.spacing(0, 0),
  },
  graphCardBody: {
    display: 'grid',
    minHeight: 224,
  },
  graphCardMessage: {
    display: 'grid',
    justifyItems: 'center',
  },
}))
